import {
    PropsWithChildren,
    FC,
    useLayoutEffect,
    useRef,
    Children,
    cloneElement,
    ReactNode,
    ReactElement,
    isValidElement,
    MouseEventHandler,
    FocusEventHandler,
} from 'react';
import classnames from 'classnames';

import { CheckableChip } from '@hh.ru/magritte-ui-chips/CheckableChip';
import { CustomChip } from '@hh.ru/magritte-ui-chips/CustomChip';
import type { CheckableChipProps, ChipsContainerProps, CustomChipProps } from '@hh.ru/magritte-ui-chips/types';
import { ScrollableContainer, useScrollContext } from '@hh.ru/magritte-ui-scrollable-container';

import styles from './chips-container.less';

const isValidCheckableChipElement = (child: ReactNode): child is ReactElement<CheckableChipProps> =>
    isValidElement(child) && child.type === CheckableChip;

const isValidCustomChipElement = (child: ReactNode): child is ReactElement<CustomChipProps> =>
    isValidElement(child) && child.type === CustomChip;

const ContainerItem = ({
    children,
}: {
    children: ReactElement<CheckableChipProps | CustomChipProps>;
}): ReactElement => {
    const ref = useRef<HTMLDivElement>(null);
    const mouseDownRef = useRef(false);
    const { registerItem, removeItem, scrollToItem } = useScrollContext();
    useLayoutEffect(() => {
        registerItem(ref);
        return () => removeItem(ref);
    }, [registerItem, removeItem]);

    const handleClick: MouseEventHandler<HTMLElement> = (event) => {
        (children.props as React.DOMAttributes<HTMLElement>).onClick?.(event);
        scrollToItem(ref);
        mouseDownRef.current = false;
    };

    const handleFocus: FocusEventHandler<HTMLElement> = (event) => {
        (children.props as React.DOMAttributes<HTMLElement>).onFocus?.(event);
        // Проверка нужна для случая, когда клик длинный. Если не проверять, то произойдет скролл
        // и onClick не вызовется, потому что будет mousemove
        if (!mouseDownRef.current) {
            scrollToItem(ref);
        }
    };

    const handleMouseDown: MouseEventHandler<HTMLElement> = (event) => {
        (children.props as React.DOMAttributes<HTMLElement>).onMouseDown?.(event);
        mouseDownRef.current = true;
    };

    return (
        <div ref={ref} className={styles.item}>
            {cloneElement(children, {
                onClick: handleClick,
                onFocus: handleFocus,
                onMouseDown: handleMouseDown,
            })}
        </div>
    );
};

export const ChipsContainer: FC<PropsWithChildren<ChipsContainerProps>> = ({
    children,
    noWrap = false,
    scrollable = false,
    ...containerProps
}) => {
    const filteredChildren = Children.toArray(children).filter(
        (child): child is ReactElement<CheckableChipProps> | ReactElement<CustomChipProps> =>
            isValidCheckableChipElement(child) || isValidCustomChipElement(child)
    );

    if (!scrollable) {
        return (
            <div className={styles.container} data-qa="chips-container" {...containerProps}>
                {filteredChildren}
            </div>
        );
    }

    return (
        <ScrollableContainer disableButtons={!noWrap} data-qa="chips-container" {...containerProps}>
            <div className={classnames(styles.container, styles.scrollable, noWrap && styles.nowrap)}>
                {Children.map(filteredChildren, (child) => (
                    <ContainerItem>{child}</ContainerItem>
                ))}
            </div>
        </ScrollableContainer>
    );
};
